import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { getUserProfile } from '@store/common/selectors';
import { ErrorBoundary } from '@components/common';
import { Button, Input, Select, Space, message, Switch, Alert } from 'antd';
import { useAtomValue } from 'jotai';
import {
  transformFilterMetaToFields,
  transformFilterRulesToQuery,
  convertQueryToFilterRules,
  queryValidator,
  type Query,
} from '@components/filter/components/common/FilterRulesBuilder';
import {
  DataSource,
  parseRawCategory,
  disabledOperators,
  isInvalidXMLString,
} from '@components/filter/components/utils';
import { APP_DIMENSION_NAME, VIDEO_DIMENSION_NAME } from 'components/filter/components/constant';
import {
  getMatchedCategory,
  getCategoryOptions,
} from '@components/filter/components/common/AdvanceFilterBuilderForm/utils';
import FilterRules from './FilterRules';
import * as atoms from '../../atom';
import * as Types from '../../../../types';
import * as Styled from './styled';

export interface FilterBuilderPanelProps {
  id?: number;
  filter?: Types.Filter;
  saveButtonText?: string;
  cancelButtonText?: string;
  isPolicy?: boolean;
  onSave: (filter: Types.Filter, id?: number) => Promise<void>;
  onCancel: () => void;
}

const formatCategoryStr = (category: string, subcategory: string) => `${category}/${subcategory}`;

const checkFilterErrors = (name: string, query: Query) => {
  const isValidQuery = queryValidator(query);
  const errors = {} as Record<
    'name' | 'query',
    {
      valid: boolean;
      message?: string;
    }
  >;
  if (!isValidQuery) {
    const message = !query.rules?.length ? 'Please add filter rules' : undefined;
    errors.query = {
      valid: false,
      message,
    };
  }
  if (!name) {
    errors.name = {
      valid: false,
      message: 'Filter name is required',
    };
  } else if (!/^[0-9A-Za-z]/.test(name)) {
    errors.name = {
      valid: false,
      message: 'The first character must be alphanumeric',
    };
  } else if (isInvalidXMLString(name)) {
    errors.name = {
      valid: false,
      message: `Filter name '${name}' has invalid characters which are not supported.`,
    };
  }
  return errors;
};

export const FilterBuilderPanel = (props: FilterBuilderPanelProps) => {
  const { id, filter, saveButtonText, cancelButtonText, isPolicy, onSave, onCancel } = props;
  const [showError, setShowError] = useState(false);
  const user = useSelector(getUserProfile);
  const filterMeta = useAtomValue(atoms.filterMeta);
  const dataSource = useAtomValue(atoms.dataSource);
  const isFilterCreate = useAtomValue(atoms.filterCreate);
  const fields = useMemo(
    () => transformFilterMetaToFields(filterMeta, { disabledOperators, isPolicy }),
    [filterMeta, isPolicy],
  );

  // For app it would be platform, for video/ad it would be device name
  const defaultField =
    dataSource === DataSource.app
      ? fields.find((it) => it.name === APP_DIMENSION_NAME.platform)
      : fields.find((it) => it.name === VIDEO_DIMENSION_NAME.deviceName);

  const [query, setQuery] = useState(() => transformFilterRulesToQuery(filter?.rule));

  // Filter name
  const [currentName, setCurrentName] = useState(filter?.name || '');

  // Filter hidden
  // Pick the value of filter.hidden first, by default it use the value of user.hidden
  const [hidden, setHidden] = useState<boolean | undefined>(
    filter?.isHiddenFromCustomer ?? user.hidden,
  );

  const categoryOptions = useMemo(() => getCategoryOptions(filterMeta), [filterMeta]);

  // Filter category & subcategory
  const [currentCategoryStr, setCurrentCategoryStr] = useState(() =>
    filter
      ? formatCategoryStr(filter.category, filter.subcategory)
      : getMatchedCategory(
          get(query, 'rules[0].field') ?? get(query, 'rules[0].rules[0].field'),
          filterMeta,
          categoryOptions,
        ),
  );

  const autoUpdateCategoryStr = (str) => {
    if (!isFilterCreate) return;
    setCurrentCategoryStr(str);
  };

  const errors = checkFilterErrors(currentName, query);
  const handleOnSave = () => {
    if (Object.keys(errors).length) {
      if (errors.query?.message) {
        message.warning(errors.query.message);
      }
      const firstErrorInput = document.querySelector('.filter-builder-panel .error-input');
      firstErrorInput?.scrollIntoView();
      setShowError(true);
      return;
    }
    setShowError(false);

    const { category, subcategory } = parseRawCategory(currentCategoryStr!);
    const currentFilter = {
      name: currentName,
      rule: convertQueryToFilterRules(query),
      category,
      subcategory,
    } as Types.Filter;
    if (typeof hidden === 'boolean') {
      Object.assign(currentFilter, { isHiddenFromCustomer: hidden });
    }

    onSave(currentFilter, id);
  };

  const renderHiddenFilterSwitch = () => {
    if (!user.hidden) return null;
    return (
      <Styled.FormItemContainer style={{ paddingLeft: '60px' }}>
        <Space direction="horizontal">
          <Switch
            checked={hidden}
            onChange={(v) => {
              setHidden(v ?? false);
            }}
          />
          <span>For Conviva internal view only</span>
        </Space>
      </Styled.FormItemContainer>
    );
  };

  return (
    <ErrorBoundary>
      <Styled.Container className="filter-builder-panel">
        <Styled.Wrapper>
          {isPolicy && (
            <Alert
              message="Precision Policy filter"
              description="Editing a Precision Policy filter can impact the Precision traffic. Please make sure to review the policies before making the changes."
              type="warning"
              showIcon
              style={{
                marginBottom: 20,
              }}
            />
          )}
          <Styled.FormItemContainer>
            <FilterRules
              defaultField={defaultField}
              fields={fields}
              query={query}
              showError={showError}
              onChange={(q) => {
                setQuery(q);
                setShowError(false);
                autoUpdateCategoryStr(
                  getMatchedCategory(
                    get(q, 'rules[0].field') ?? get(q, 'rules[0].rules[0].field'),
                    filterMeta,
                    categoryOptions,
                  ),
                );
              }}
            />
          </Styled.FormItemContainer>
          <Styled.FormItemContainer style={{ paddingLeft: '60px' }}>
            <Space direction="vertical">
              <Styled.FormItemLabel required>Filter Name</Styled.FormItemLabel>
              <Input
                maxLength={128}
                autoComplete="off"
                placeholder="Please enter filter name"
                className={errors.name && showError ? 'error-input' : ''}
                style={{
                  width: '400px',
                }}
                value={currentName}
                onChange={(e) => {
                  setCurrentName(e.target.value);
                  setShowError(false);
                }}
              />
              {errors.name && showError ? (
                <Styled.ErrorMessage>{errors.name.message}</Styled.ErrorMessage>
              ) : null}
            </Space>
            <Space direction="vertical">
              <Styled.FormItemLabel required>Category</Styled.FormItemLabel>
              <Select
                showSearch
                style={{
                  width: '250px',
                }}
                value={currentCategoryStr}
                options={categoryOptions}
                onChange={(data) => setCurrentCategoryStr(data)}
              />
            </Space>
          </Styled.FormItemContainer>
          {renderHiddenFilterSwitch()}
          <Styled.FormItemContainer
            style={{ paddingTop: '20px', borderTop: '1px solid rgba(153, 153, 153, 0.3)' }}
          >
            <Button ghost type="primary" onClick={handleOnSave}>
              {saveButtonText}
            </Button>
            <Button onClick={onCancel}>{cancelButtonText || 'Cancel'}</Button>
          </Styled.FormItemContainer>
        </Styled.Wrapper>
      </Styled.Container>
    </ErrorBoundary>
  );
};
